<div class="pid-dialog">

  <!-- =====================================================
       HEADER
       ===================================================== -->

  <div class="pid-header">

    <div class="pid-header-left">

      @if (isDhl) {
        <div class="dhl-wordmark" aria-label="DHL">
          <img src="assets/images/dhl-logo-cropped.png" alt="DHL" />
        </div>
      } @else {
        <div class="fedex-wordmark" aria-label="FedEx">
          <span class="fedex-fed">Fed</span><span class="fedex-ex">Ex</span>
        </div>
      }

      <div class="pid-header-separator"></div>

      <div class="pid-title">
        {{ "app.pid.title" | translate }}
      </div>

    </div>

    <button
      type="button"
      class="pid-close"
      [attr.aria-label]="'app.pid.close' | translate"
      (click)="cancel()">
      &times;
    </button>

  </div>


  <!-- =====================================================
       CONTENT
       ===================================================== -->

  <div class="pid-content">


    <!-- ===================================================
         BLUE INFORMATION BLOCK
         =================================================== -->

    <div class="pid-information">

      <div class="pid-information__icon">
        i
      </div>

      <div class="pid-information__text">

        <div class="pid-information__title">
          {{ isDhl ? dhlInformationTitle : ("app.pid.additionalInformationTitle" | translate) }}
        </div>

        <div class="pid-information__description">
          {{ informationDescription }}
        </div>

      </div>

    </div>



    <!-- ===================================================
         PRODUCT INFORMATION
         =================================================== -->

    <div class="product-section">


      <div class="product-section__title">
        {{ "app.pid.productInformation" | translate }}
      </div>


      <div class="product-section__description">
        {{ "app.pid.productInformationDescription" | translate }}
      </div>


      <!-- =================================================
           UNIT OF MEASURE
           ================================================= -->

      <div class="product-grid-info">

        <span>
          {{ "app.pid.unitOfMeasure" | translate }}
        </span>

        <strong>
          {{ unitOfMeasureTranslationKey | translate }}
        </strong>

      </div>


      <!-- =================================================
           PRODUCT TABLE
           ================================================= -->

      <div class="product-table-wrapper">

        <table class="product-table">


          <colgroup>

            <col class="col-number" />

            <col class="col-description" />

            <col class="col-country" />

            <col class="col-hs" />

            <col class="col-qty" />

            <col class="col-weight" />

            <col class="col-value" />

            <col class="col-sku" />

            <col class="col-manufacturer" />

            <col class="col-gtin" />

          </colgroup>


          <!-- =============================================
               TABLE HEADER
               ============================================= -->

          <thead>

            <tr>


              <th class="center">
                #
              </th>


              <th>
                {{ "app.pid.description" | translate }}
              </th>


              <th>
                {{ "app.pid.countryOfOrigin" | translate }}
              </th>


              <th>
                {{ "app.pid.hsCode" | translate }}
              </th>


              <th class="center">
                {{ "app.pid.quantity" | translate }}
              </th>


              <th class="center">
                {{ "app.pid.unitWeight" | translate }}
              </th>


              <th class="center">
                {{ "app.pid.unitValue" | translate }}
              </th>


              <!-- =========================================
                   SKU / M-PID
                   ========================================= -->

              <th class="pid-column-header">

                <div class="pid-header-label">

                  <span>
                    {{ "app.pid.sku" | translate }}
                    <span class="required">*</span>
                  </span>

                  <span
                    class="info-icon info-icon--blue"
                    [attr.title]="'app.pid.skuTooltip' | translate">
                    i
                  </span>

                </div>

              </th>


              <!-- =========================================
                   MANUFACTURER PART / NS-PID
                   ========================================= -->

              <th class="pid-column-header">

                <div class="pid-header-label">

                  <span>
                    {{ "app.pid.manufacturerPartNumber" | translate }}
                    <span class="required">*</span>
                  </span>

                  <span
                    class="info-icon info-icon--blue"
                    [attr.title]="'app.pid.manufacturerPartNumberTooltip' | translate">
                    i
                  </span>

                </div>

              </th>


              <!-- =========================================
                   GTIN / S-PID
                   ========================================= -->

              <th class="pid-column-header">

                <div class="pid-header-label">

                  <span>
                    {{ "app.pid.gtin" | translate }}
                  </span>

                  <span
                    class="info-icon info-icon--blue"
                    [attr.title]="'app.pid.gtinTooltip' | translate">
                    i
                  </span>

                </div>

              </th>


            </tr>

          </thead>


          <!-- =============================================
               PRODUCTS
               ============================================= -->

          <tbody>


            @for (
              item of products;
              track item.productIndex;
              let rowIndex = $index
            ) {

              <tr>


                <!-- # -->

                <td class="center">
                  {{ rowIndex + 1 }}
                </td>


                <!-- DESCRIPTION -->

                <td>
                  {{ item.product.ItemDescription || item.product.ItemName }}
                </td>


                <!-- COUNTRY OF ORIGIN -->

                <td>
                  {{ item.product.CountryOfOrigin }}
                </td>


                <!-- HS CODE -->

                <td>
                  {{ item.product.HSCode }}
                </td>


                <!-- QUANTITY -->

                <td class="center">
                  {{ item.product.Quantity }}
                </td>


                <!-- UNIT WEIGHT -->

                <td class="center">
                  {{ item.product.Weight }}
                </td>


                <!-- UNIT VALUE -->

                <td class="center">
                  {{ item.product.DeclaredUnitValue }}
                </td>


                <!-- =======================================
                     SKU / M-PID
                     ======================================= -->

                <td>

                  <input
                    type="text"
                    class="pid-input"
                    maxlength="70"
                    autocomplete="off"
                    [(ngModel)]="item.sku"
                    [disabled]="!pidEnabled"
                    [class.pid-input--invalid]="
                      pidRequired &&
                      !item.sku.trim()
                    " />

                </td>


                <!-- =======================================
                     MANUFACTURER PART / NS-PID
                     ======================================= -->

                <td>

                  <input
                    type="text"
                    class="pid-input"
                    maxlength="70"
                    autocomplete="off"
                    [(ngModel)]="item.manufacturerPartNumber"
                    [disabled]="!pidEnabled"
                    [class.pid-input--invalid]="
                      pidRequired &&
                      !item.manufacturerPartNumber.trim()
                    " />

                </td>


                <!-- =======================================
                     GTIN / UPC / EAN / S-PID
                     ======================================= -->

                <td>

                  <input
                    type="text"
                    class="pid-input"
                    maxlength="70"
                    autocomplete="off"
                    [(ngModel)]="item.gtinUpcEan"
                    [disabled]="!pidEnabled" />

                </td>


              </tr>

            }


          </tbody>

        </table>

      </div>


      <!-- =================================================
           WARNING
           ================================================= -->

      <div class="pid-warning">

        <div class="pid-warning__icon">
          !
        </div>


        <div class="pid-warning__content">

          <div class="pid-warning__title">
            {{ warningTitle }}
          </div>

          <div class="pid-warning__text">
            {{ warningText }}
          </div>

        </div>

      </div>


    </div>

  </div>


  <!-- =====================================================
       FOOTER
       ===================================================== -->

  <div class="pid-footer">

    <button
      type="button"
      class="pid-button pid-button--cancel"
      (click)="cancel()">
      {{ "app.pid.cancel" | translate }}
    </button>


    <button
      type="button"
      class="pid-button pid-button--ok"
      (click)="confirm()">
      {{ "app.pid.ok" | translate }}
    </button>

  </div>

</div>